import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const STACK_OFFSET_UNITS = 18

// --spacing is a fluid calc()/vw expression, so its pixel value only exists
// once the browser resolves it against a real box. Reading it straight off
// getComputedStyle would just hand back the unresolved calc() string.
const getStackOffset = () => {
    const probe = document.createElement('div')
    probe.style.cssText = `position:absolute; visibility:hidden; height:0; width:calc(var(--spacing) * ${STACK_OFFSET_UNITS})`
    document.body.appendChild(probe)
    const offset = probe.getBoundingClientRect().width
    probe.remove()
    return offset
}

const initBlockHorCardsStack = () => {
    const containers = document.querySelectorAll('.blockHorCards')
    if (!containers.length) return

    const mm = gsap.matchMedia()

    mm.add('(min-width: 1024px)', () => {
        const cleanups = []

        containers.forEach((container) => {
            const cards = Array.from(
                container.querySelectorAll('[data-hor-card]')
            )

            if (cards.length < 2) return

            cards.forEach((card, index) => {
                gsap.set(card, { position: 'relative', zIndex: index + 1 })
            })

            // How far each card has to travel (in its natural, unstacked
            // layout) to reach its resting spot: the first card doesn't move,
            // each next one rests STACK_OFFSET below the previous.
            let travel = cards.map(() => 0)
            let totalDistance = 0

            // Reverts our own transforms/height first so measurements reflect
            // real layout, not a stale mid-animation state. Run from
            // onRefreshInit (fires before GSAP recalculates start/end) rather
            // than from the "end" resolver, which can be invoked at other,
            // less predictable points and produced inconsistent results.
            const measure = () => {
                const stackOffset = getStackOffset()

                gsap.set(cards, { y: 0, clearProps: 'height' })
                const anchor =
                    cards[0].getBoundingClientRect().top -
                    container.getBoundingClientRect().top

                // The pin fixes the whole block to the viewport, so a card
                // whose aspect-ratio-driven height doesn't fit the space left
                // once stacked would get clipped. Cap every card to whatever
                // height still fits at the last (most cramped) resting spot.
                const lastRestOffset =
                    anchor + (cards.length - 1) * stackOffset
                const maxCardHeight = Math.max(
                    window.innerHeight - lastRestOffset,
                    320
                )
                gsap.set(cards, { height: maxCardHeight })

                const containerTop = container.getBoundingClientRect().top
                travel = cards.map((card, index) => {
                    const natural =
                        card.getBoundingClientRect().top - containerTop
                    return Math.max(
                        natural - (anchor + index * stackOffset),
                        0
                    )
                })
                totalDistance = Math.max(...travel)
            }

            measure()

            const scrollTrigger = ScrollTrigger.create({
                trigger: container,
                start: 'top top',
                end: () => `+=${totalDistance}`,
                pin: true,
                pinSpacing: false,
                scrub: true,
                onRefreshInit: measure,
                onUpdate: (self) => {
                    const scrolled = self.progress * totalDistance

                    cards.forEach((card, index) => {
                        gsap.set(card, {
                            y: -Math.min(scrolled, travel[index])
                        })
                    })
                }
            })

            cleanups.push(() => {
                scrollTrigger.kill()
                gsap.set(cards, {
                    clearProps: 'position,zIndex,transform,height'
                })
            })
        })

        return () => cleanups.forEach((cleanup) => cleanup())
    })
}

document.addEventListener('DOMContentLoaded', () => {
    initBlockHorCardsStack()
})

export { initBlockHorCardsStack }
